import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { MockService } from 'types/rest/mockServiceTypes';
import { useIsMount } from 'hooks/useIsMountHook';
import _ from 'lodash';
import i18n from 'i18n';
import { getTransactionsForPriorityModal, updateTransactionPriority } from './transactionPriorityModalActions';
import DraggableTransactionsPriorityTable, { Move } from './DraggableTransactionsPriorityTable';
import { Button } from 'react-bootstrap';
import { Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import { getSelectedCountTitle, moveElementsToEnd, moveElementToBeginning } from 'utils/miscUtil';
import BzmIcon from 'components/icons/BzmIcon';
import { useDispatch } from 'react-redux';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import SimpleLoader from 'components/loaders/SimpleLoader';

interface TransactionPriorityModalProps {
  /**
   * Modal hide event.
   * @param updatedEntity - modified entity (optional)
   */
  onHide: (updatedEntity?: MockService | MockServiceTemplate) => void;
  context: MockService | MockServiceTemplate;
  type: TransactionType;
}
const TransactionPriorityModal = (props: TransactionPriorityModalProps) => {
  const isMount = useIsMount();
  const [transactions, setTransactions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  const getTransactionsForEntity = async () => {
    setTransactions(await getTransactionsForPriorityModal(props.context, props.type));
  };

  //On mount
  useEffect(() => {
    if (isMount) {
      getTransactionsForEntity().then(() => {
        setIsLoading(false);
      });
    }
  }, []);

  const getCounterValue = useMemo(() => {
    return getSelectedCountTitle(_.size(transactions), _.size(selected));
  }, [selected]);

  const savePrioritiesHandler = async () => {
    setIsSaving(true);
    const updated = await updateTransactionPriority(props.context, transactions);
    await new Promise(resolve => setTimeout(resolve, 500));
    showSuccessMessage();
    props.onHide(updated);
  };

  /**
   * Reassigns priority of transactions based on the order in the array passed.
   * @param items
   */
  const recalculatePriority = (items: Transaction[]) => {
    let counter = 1;
    const recalculated = _.map(items, (item: Transaction) => {
      return { ...item, priority: counter++ };
    });
    return recalculated;
  };

  const showSuccessMessage = () => {
    dispatch(showToastNotification(ToastType.Success, 'Transaction order has been successfully updated.'));
  };

  const moveTransactionHandler = (transaction: Transaction, moveType: Move) => {
    let updatedTxns = _.cloneDeep(transactions);
    if (moveType === Move.start) {
      updatedTxns = moveElementToBeginning(updatedTxns, [transaction] as any);
    } else {
      updatedTxns = moveElementsToEnd(updatedTxns, [transaction] as any[]);
    }
    updatedTxns = recalculatePriority(updatedTxns);
    setTransactions(updatedTxns);
    //showSuccessMessage();
  };

  /**
   * Map selected transaction ids to transactions.
   */
  const getSelectedTransactions = () => {
    return _.map(selected, element => {
      return transactions.find(txn => txn.id === element);
    });
  };

  /**
   * Bulk move of multiple transactions to beginning / end.
   * @param moveType
   */
  const moveSelectedTransactionsInBulk = (moveType: Move) => {
    const toMove = getSelectedTransactions();
    let updated: any[];
    if (moveType === Move.start) {
      updated = moveElementToBeginning(transactions, toMove);
    } else {
      updated = moveElementsToEnd(transactions, toMove);
    }
    updated = recalculatePriority(updated);
    setTransactions(updated);
    setSelected([]);
    //showSuccessMessage();
  };

  const getActionButtons = () => {
    const txnCount = _.size(transactions);
    const selectedCount = _.size(selected);
    const actionDisabled = selectedCount === 0 || txnCount === selectedCount;
    return (
      <div className={'d-inline-block ml-5'}>
        <Button variant={'link'} className={'btn-no-hover p-0'} disabled={actionDisabled}>
          <BzmIcon
            size={16}
            className={'hand-cursor'}
            variant={'icon-bzm-move-up'}
            onClick={() => moveSelectedTransactionsInBulk(Move.start)}
          />
        </Button>
        <Button variant={'link'} className={'btn-no-hover p-0'} disabled={actionDisabled}>
          <BzmIcon
            size={16}
            className={'hand-cursor pl-2'}
            variant={'icon-bzm-move-down'}
            onClick={() => moveSelectedTransactionsInBulk(Move.end)}
          />
        </Button>
      </div>
    );
  };

  return (
    <Modal
      animation={false}
      show={true}
      backdrop={true}
      onHide={props.onHide}
      size="xl"
      dialogClassName="modal-full-screen"
    >
      <Modal.Header closeButton>
        <h4 className="modal-title">{i18n.t('mockServices:priorityModal.title')}</h4>
      </Modal.Header>
      <Modal.Body>
        <p>{i18n.t('mockServices:priorityModal.desc')}</p>
        <h4>
          Transactions {getCounterValue}
          {getActionButtons()}
        </h4>
        <DraggableTransactionsPriorityTable
          transactions={transactions}
          isLoading={isLoading}
          context={props.context}
          onSelectionChange={(selectedItems: any[]) => {
            setSelected(selectedItems);
          }}
          moveTransaction={moveTransactionHandler}
          onDragFinished={(items: Transaction[]) => {
            setTransactions(recalculatePriority(items));
          }}
          selectedItems={selected}
          type={props.type}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => props.onHide()}>
          {i18n.t('common:actions.cancel')}
        </Button>
        <Button onClick={savePrioritiesHandler} disabled={isSaving}>
          {isSaving && <SimpleLoader className={'d-inline-block mr-3'} showLoadingText={false} />}
          {i18n.t('common:actions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransactionPriorityModal;
